import { useCallback, useMemo } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ChevronLeft, LifeBuoy } from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, FetchParams } from '@/components/DataTable/types'
import { fetchUserTickets, type AdminTicket } from '@/lib/adapters'
import { PERMISSIONS } from '@/lib/permissions'

/**
 * A3.20 — Foydalanuvchi support-tiketlari. GET /admin/users/:id/tickets
 * (backend `bbb3fa7`). Foydalanuvchi kartasidan kirish.
 */
export function UserTickets() {
  const t = useT()
  const { id = '' } = useParams()

  const columns = useMemo<AdminColumn<AdminTicket>[]>(
    () => [
      { key: 'id', header: t.col.id, type: 'id', width: 70 },
      {
        key: 'subject',
        header: t.support.subject,
        type: 'text',
        minWidth: 240,
        sortable: false,
        render: (r) => (
          <Link to={`/support/${r.id}`} className="text-accent hover:underline">
            {r.subject || `#${r.id}`}
          </Link>
        ),
      },
      { key: 'category', header: t.mod.category, type: 'text', width: 120 },
      {
        key: 'status',
        header: t.col.status,
        type: 'enum',
        width: 120,
        options: [
          { value: 'open', label: t.support.open, status: 'open' },
          { value: 'pending', label: t.support.pending, status: 'pending' },
          { value: 'closed', label: t.support.closed, status: 'deleted' },
        ],
      },
      { key: 'priority', header: t.userExtra.priority, type: 'text', width: 100 },
      { key: 'updated_at', header: t.support.updated, type: 'date', width: 160 },
    ],
    [t],
  )

  const fetcher = useCallback((params: FetchParams) => fetchUserTickets(Number(id), params), [id])

  return (
    <>
      <PageHeader
        title={t.userExtra.ticketsTitle}
        screenId="A3.20"
        actions={
          <Link
            to={`/users/${id}`}
            className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
          >
            <ChevronLeft size={13} />
            {t.users.cardTitle}
          </Link>
        }
      />
      <DataTable<AdminTicket>
        screenId="A3.20"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        emptyState={{ icon: <LifeBuoy size={28} strokeWidth={1.5} /> }}
      />
    </>
  )
}
